import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PriceForm from './PriceForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import ReportLauncher from '../../../shared/ReportLauncher';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import PriceBreakReportView from './PriceBreakReportView';
import MarginReportView from './MarginReportView';
import MarginMatrixReportView from './MarginMatrixReportView';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { Pill, AvailabilityPill } from '../../../shared/Pill';
import { Filter as FilterIcon, ChevronRight } from 'lucide-react';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import DateInput from '../../../shared/DateInput';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney, formatNumber, formatPercent } from '../../../shared/numberFormat';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

function formatRuleTiers(rule) {
  const tiers = rule.tiers || [];
  if (tiers.length === 0) return '';
  const lowest = tiers[0];
  const formatted = rule.type === 'percent_off' ? formatPercent(lowest.amount) : formatMoney(lowest.amount);
  return tiers.length === 1 ? formatted : `${formatted} (+${tiers.length - 1} more)`;
}

function formatBreaks(breaks) {
  if (!breaks || breaks.length === 0) return '—';
  const lowest = breaks[0];
  const formatted = formatMoney(lowest.price);
  return breaks.length === 1 ? formatted : `${formatted} (+${breaks.length - 1} more)`;
}

export function AdjustmentTypeCell({ item }) {
  if (item.rule) {
    return <span>{item.rule.name} ({formatRuleTiers(item.rule)})</span>;
  }
  if (item.needs_price) {
    // "Needs pricing" — nothing at all has been set up yet (still the
    // default open-ended "Always" period, no Price Break entered).
    // "Needs Price Breaks" — a real Effective window has already been
    // scheduled, it's just this period's own Price Break that's missing —
    // a narrower, less alarming gap than the first case.
    const isScheduled = !!(item.start_date || item.end_date);
    return isScheduled
      ? <Pill color="#b45309" background="#fdf1e0" title="This period has an Effective window set but no Price Break entered yet">Needs Price Breaks</Pill>
      : <Pill color="#b45309" background="#fdf1e0" title="This Fixed Override has no Price Break entered yet">Needs pricing</Pill>;
  }
  return <span>Fixed {formatBreaks(item.breaks)}</span>;
}

function fullScheduleTitle(item) {
  if (item.rule) {
    const tiers = item.rule.tiers || [];
    if (tiers.length <= 1) return '';
    return tiers
      .map((tier) => `${formatNumber(tier.min_quantity, { decimals: 0 })}+ → ${item.rule.type === 'percent_off' ? formatPercent(tier.amount) : formatMoney(tier.amount)}`)
      .join('\n');
  }
  const breaks = item.breaks || [];
  if (breaks.length <= 1) return '';
  return breaks
    .map((brk) => `${formatNumber(brk.min_quantity, { decimals: 0 })}+ → ${formatMoney(brk.price)}`)
    .join('\n');
}

export function formatProductScope(item) {
  if (item.product) return item.product.sku;
  if (item.product_price_group) return item.product_price_group.short_code || item.product_price_group.name;
  return '(all products)';
}

export function formatCustomerScope(item) {
  if (item.customer) return item.customer.name;
  if (item.customer_price_group) return item.customer_price_group.short_code || item.customer_price_group.name;
  return '(all customers)';
}

// Full group name, for a hover title on cells that show only the short code
export function productScopeTitle(item) {
  return item.product_price_group ? item.product_price_group.name : undefined;
}

export function customerScopeTitle(item) {
  return item.customer_price_group ? item.customer_price_group.name : undefined;
}

// Identifies which Scope (Product/Customer Scope + UoM) a row belongs to —
// the same identity the backend's own findOverlappingPrice/loadScheduleForScope
// group by. Rows arrive from /api/prices already sorted by this same Scope
// order, so consecutive rows sharing a key can just be merged in one pass
// below rather than grouped by search/sort.
function scopeKey(item) {
  return [item.product_id || '', item.product_price_group_id || '', item.customer_id || '', item.customer_price_group_id || '', item.uom_id || ''].join('|');
}

function groupRows(rows) {
  const groups = [];
  rows.forEach((item) => {
    const key = scopeKey(item);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(item);
    } else {
      groups.push({ key, items: [item] });
    }
  });
  return groups;
}

export function EffectiveDates({ item }) {
  if (!item.start_date && !item.end_date) return <span>Always</span>;
  return (
    <>
      <div>{formatDate(item.start_date) || '(any)'} –</div>
      <div>{formatDate(item.end_date) || '(any)'}</div>
    </>
  );
}

// Purely date-derived now — a Price is either inside its own Effective
// window (Current), hasn't started yet (Future), or has ended (Past). No
// separate active/inactive state to layer on top. Shared by every screen
// that shows a Price's status (list, Pricing page Schedule, Margin/Price
// Break reports) so the label never drifts out of sync between them.
// "Future" over "Upcoming" deliberately — "Upcoming" reads as "any day
// now," which is misleading for a date range years out; "Future" is a
// plain, neutral counterpart to "Past" regardless of how far off the
// start date is. The underlying 'upcoming' status key is unchanged —
// this is a display-label-only rename.
export function statusLabel(status) {
  if (status === 'upcoming') return 'Future';
  if (status === 'past') return 'Past';
  return 'Current';
}

export function StatusPill({ item }) {
  if (item.status === 'upcoming') {
    return <Pill color="#1976d2" background="rgba(25, 118, 210, 0.12)">Future</Pill>;
  }
  if (item.status === 'past') {
    return <Pill color="#5a6b85" background="#dde3ea">Past</Pill>;
  }
  return <Pill color="#1a7f4b" background="rgba(26, 127, 75, 0.12)">Current</Pill>;
}

const STATUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'current', label: 'Current' },
  { key: 'upcoming', label: 'Future' },
  { key: 'past', label: 'Past' },
];

const METHOD_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'fixed', label: 'Fixed' },
  { key: 'rule', label: 'Rule' },
];

// Identifies this screen's saved filters (SavedFilterTabs) from any other
// screen's — one saved_filters table serves every screen as this pattern
// rolls out (pat-architecture-filter-bar.md, once written), not just this
// pilot.
const FILTER_SCREEN_KEY = 'prices';

// Optional list-table columns (Columns picker, persisted per-user via
// /api/column-preferences) — same shared ColumnPicker pattern as
// ProductTable.js. Customer/Product/UoM/Type/Effective/actions stay
// always-visible (SYSTEM_COLUMNS); Product Name, Availability and Status are
// the optional columns.
// Few enough to all start on and still fit a laptop width
// (ui-architecture.md Column Picker).
const OPTIONAL_COLUMNS = [
  { key: 'product_name', label: 'Product Name' },
  { key: 'availability', label: 'Availability' },
  { key: 'status', label: 'Status' },
];
const SYSTEM_COLUMNS = ['Customer', 'SKU', 'UoM', 'Type', 'Effective', 'Actions'];
const DEFAULT_VISIBLE_COLUMNS = OPTIONAL_COLUMNS.map((c) => c.key);

// The fixed baseline SavedFilterTabs' Default tab always resets to. Status
// lives in this object like every other field — a saved tab (built via the
// drawer) can pin it, same as Category/Customer Group/UoM/dates, since a
// preset should be free to capture anything from broad to precise, however
// often it actually gets used. Only Product search/UoM/dates also get a
// second, always-visible "quick row" home for ad hoc tweaking that isn't
// worth opening the drawer to save.
const DEFAULT_FILTERS = {
  status: 'all',
  method: 'all',
  categoryId: '',
  productSearch: '',
  // Exact-Product pin — deliberately separate from productSearch above,
  // which is a substring match (SP400 also matches SP400BK, by design, for
  // browsing a whole SKU family). A saved tab meant to pin one specific
  // Product needs a filter that can't accidentally widen as new SKUs are
  // added later; productLabel just carries the display text along so the
  // drawer can show what's pinned without re-fetching the Product on open.
  productId: '',
  productLabel: '',
  customerGroupId: '',
  uomId: '',
  startDate: '',
  endDate: '',
};

const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Leading whitespace (even repeated non-breaking spaces) gets trimmed by
// the OS-drawn native <select className="styled-input"> popup on Windows, so plain indentation is
// invisible there even though it renders fine as normal HTML text — a
// non-whitespace marker per depth level is the only thing guaranteed to
// survive that trim.
// Same non-breaking-space indent convention as the Product list's own
// Category dropdown (ProductTable.js) - plain spaces collapse to one in
// rendered <option> text, so this needs \u00A0, not ' '.
function indentOptionLabel(name, depth) {
  const d = depth || 0;
  return `${'\u00A0\u00A0\u00A0'.repeat(d)}${name}`;
}

// Search-as-you-type Product picker resolving to one exact product_id —
// same pattern as PriceForm's own Product Scope picker, standalone
// here since it's only used inside the saved-tab drawer (renderDrawerFields
// is a plain function called mid-render, not a component, so it can't hold
// hooks itself).
function ProductExactPicker({ productId, productLabel, onChange }) {
  const [search, setSearch] = useState(productLabel || '');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const skipNextSearch = useRef(true);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    if (!search.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(() => {
      axios.get('/api/products', { params: { search, status: 'active', limit: 10 } })
        .then((res) => { setResults(res.data.data || []); setShowResults(true); })
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSelect = (p) => {
    skipNextSearch.current = true;
    const label = `${p.sku} — ${p.name}`;
    setSearch(label);
    setShowResults(false);
    onChange(String(p.id), label);
  };

  const handleClear = () => {
    skipNextSearch.current = true;
    setSearch('');
    setShowResults(false);
    onChange('', '');
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={FIELD_LABEL_STYLE} title="Pins this tab to one exact Product — unlike the Product search above, this won't also match other SKUs that merely contain the same text (e.g. SP400BK when pinning SP400).">Product (exact match)</label><br />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or SKU..."
          style={FIELD_INPUT_STYLE}
        />
        {productId && (
          <button type="button" onClick={handleClear} title="Clear" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
        )}
      </div>
      {showResults && results.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, marginTop: 2, width: '100%', maxHeight: 200, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
          {results.map((p) => (
            <div key={p.id} onClick={() => handleSelect(p)} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}>
              {p.sku} — {p.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Once the drawer's Product (exact match) picker pins one SKU, scope this
// select down to just that Product's own valid UoMs (same list the Pricing
// page's Add-a-Price-Break screen offers) instead of the tenant-wide list —
// picking a UoM that SKU can't even use isn't a filter anyone means.
// Ordered by conversion_rate ascending (Piece, Pack, Box...) rather than
// insertion order, so it always reads smallest-to-largest.
function ScopedUomSelect({ productId, value, onChange, fallbackUoms }) {
  const [productUoms, setProductUoms] = useState(null);

  useEffect(() => {
    if (!productId) { setProductUoms(null); return; }
    let cancelled = false;
    axios.get(`/api/products/${productId}/uoms`)
      .then((res) => { if (!cancelled) setProductUoms(res.data.data?.uoms || []); })
      .catch(() => { if (!cancelled) setProductUoms([]); });
    return () => { cancelled = true; };
  }, [productId]);

  const options = productId
    ? [...(productUoms || [])].sort((a, b) => a.conversion_rate - b.conversion_rate)
    : fallbackUoms;
  const optionId = (u) => (productId ? u.uom_id : u.id);

  return (
    <div>
      <label style={FIELD_LABEL_STYLE} title="Matches only a row's explicit UoM - a legacy row left blank (implicit All UoMs) won't match even if that resolves to the same unit.">UoM</label><br />
      <select className="styled-input" value={value} onChange={(e) => onChange(e.target.value)} style={FIELD_INPUT_STYLE}>
        <option value="">All UoMs</option>
        {options.map((u) => (
          <option key={optionId(u)} value={optionId(u)}>{u.name}</option>
        ))}
      </select>
    </div>
  );
}

function PriceTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  // Only ever pre-fills a Duplicate (a new record seeded from an existing
  // one's Scope) — Add is the only path through PriceForm now, there's no
  // Edit, so this never carries an existing record's own id.
  const [formInitial, setFormInitial] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  // Scope groups (2+ rows sharing the same Customer/Product/UoM Scope, i.e.
  // multiple Schedule periods) start collapsed — tracks which ones the user
  // has actually opened, keyed by scopeKey (not group index) so state
  // survives a refetch.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);

  useDefaultPageSize(setLimit);

  // Filters — Product Category narrows by hierarchy (same indented-tree
  // dropdown as the Products list). Product is a broad SKU/name text
  // filter, same live-as-you-type pattern as the Products list's own
  // search (not a pick-one dropdown) — so a search like "SP400" can
  // populate the list with every matching row (SP400, SP400BK, ...) to
  // page/BrowseNav through, rather than requiring one exact Product first.
  // One object, not one useState per field, so the whole combination can
  // be applied atomically (Default/a saved filter) or saved as a single
  // blob via SavedFilterTabs, rather than seven separate setters to keep
  // in sync.
  // Lazy-init so a "?product_search=<sku>" deep link (the top search bar's
  // SKU shortcut) is baked into the very first fetch — see ProductTable.js's
  // `filters` lazy-init for why an effect-driven set would race it.
  const [filters, setFilters] = useState(() => {
    const productSearch = searchParams.get('product_search');
    // Exact-Product deep link (a Product's own record linking here, now
    // that there's no separate per-Product Prices page) — pins to one SKU
    // rather than product_search's substring match, so linking from SP400
    // never also pulls in SP400BK's rows.
    const productId = searchParams.get('product_id');
    if (productId) return { ...DEFAULT_FILTERS, productId };
    return productSearch ? { ...DEFAULT_FILTERS, productSearch } : DEFAULT_FILTERS;
  });
  const [categories, setCategories] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  // Literal uom_id match only — deliberately not resolved against a legacy
  // blank-UoM row's implicit "(All UoMs)", same "literal Scope, not
  // eligibility" philosophy as Product Category/Customer Group above; see
  // the 3-1 manual page for why (actual resolution belongs to Pricelist).
  const [uoms, setUoms] = useState([]);
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const applyFilters = (values) => {
    setFilters(values);
    setPage(1);
  };

  // Single-Row Filters (ui-architecture.md) — one "Filters" button opening
  // the same ad hoc FilterDrawer shell SavedFilterTabs uses for saving a
  // tab, applying live instead of saving. Replaces this screen's earlier
  // separate "Quick Filters ▼" inline-row toggle, which duplicated
  // renderDrawerFields in a second place and drifted from the rest of the
  // app (see ProductTable.js, which reverted off the same inline-row
  // pattern back onto this one).
  const [filterDraft, setFilterDraft] = useState(null); // null = panel closed
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };
  // productLabel is productId's own display text, not an independent
  // filter — excluded so picking one Product shows "1", not "2". The Default
  // tab has nothing built in (Status "all"), so no separate unfiltered
  // baseline is needed.
  const activeFilterCount = Object.keys(DEFAULT_FILTERS)
    .filter((k) => k !== 'productLabel')
    .filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;

  // Which report is currently open — null, 'break', 'margin', or
  // 'marginMatrix'. All three read off the exact same unpaginated Price
  // Adjustment fetch below; only the rendered view differs, so one shared
  // loading/rows/filter-summary state covers all of them.
  const [reportType, setReportType] = useState(null);
  const [reportFilterSummary, setReportFilterSummary] = useState('');
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/customer-price-groups').then((res) => setCustomerGroups((res.data.data || []).filter((g) => g.is_active))).catch(() => setCustomerGroups([]));
    axios.get('/api/product-categories').then((res) => setCategories(res.data.data || [])).catch(() => setCategories([]));
    axios.get('/api/uoms', { params: { status: 'active' } }).then((res) => setUoms(res.data.data || [])).catch(() => setUoms([]));
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [filters, page, limit]);

  // Deep link support (the top search bar's SKU shortcut): /prices
  // ?product_search=<sku> pre-filters the list (seeded into `filters`'s
  // initial state above, not here — see that comment for why). This just
  // scrubs the param once mount has read it.
  useEffect(() => {
    if (!searchParams.get('product_search') && !searchParams.get('product_id')) return;
    setSearchParams((params) => {
      params.delete('product_search');
      params.delete('product_id');
      return params;
    }, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  // Human-readable summary of the current filters, stamped onto the report
  // subtitle (pat-architecture-reports-ui-standard.md) so the report stays
  // self-documenting once printed/exported and read later without the app
  // open.
  const buildFilterSummary = () => {
    const statusLabel = STATUS_OPTIONS.find((opt) => opt.key === filters.status)?.label || 'All';
    const parts = [`Status: ${statusLabel}`];
    if (filters.method !== 'all') {
      const methodLabel = METHOD_OPTIONS.find((opt) => opt.key === filters.method)?.label || filters.method;
      parts.push(`Method: ${methodLabel}`);
    }
    if (filters.categoryId) {
      const cat = categories.find((c) => String(c.id) === String(filters.categoryId));
      parts.push(`Product Category: ${cat ? cat.name : filters.categoryId}`);
    }
    if (filters.productId) parts.push(`Product: ${filters.productLabel || filters.productId} (exact)`);
    else if (filters.productSearch) parts.push(`Product: ${filters.productSearch}`);
    if (filters.customerGroupId) {
      const group = customerGroups.find((g) => String(g.id) === String(filters.customerGroupId));
      parts.push(`Customer Price Group: ${group ? group.name : filters.customerGroupId}`);
    }
    if (filters.uomId) {
      const uom = uoms.find((u) => String(u.id) === String(filters.uomId));
      parts.push(`UoM: ${uom ? uom.name : filters.uomId}`);
    }
    if (filters.startDate) parts.push(`Effective From ≥ ${formatDate(filters.startDate)}`);
    if (filters.endDate) parts.push(`Effective To ≤ ${formatDate(filters.endDate)}`);
    return parts.join(', ');
  };

  // The list is now paginated (its own page size), so the report — which
  // needs every matching row regardless of page — issues its own separate
  // unpaginated fetch (`all: true`), same pattern as ProductTable's
  // openReport, rather than reusing the list's own paginated `rows` state.
  const openReport = async (type) => {
    setReportFilterSummary(buildFilterSummary());
    setReportType(type);
    setReportLoading(true);
    try {
      const res = await axios.get('/api/prices', {
        params: {
          status: filters.status,
          method: filters.method !== 'all' ? filters.method : undefined,
          product_category_id: filters.categoryId || undefined,
          product_search: filters.productSearch || undefined,
          product_id: filters.productId || undefined,
          customer_price_group_id: filters.customerGroupId || undefined,
          uom_id: filters.uomId || undefined,
          start_date: filters.startDate || undefined,
          end_date: filters.endDate || undefined,
          all: true,
        },
      });
      setReportRows(res.data.data || []);
    } catch (err) {
      setReportRows([]);
    }
    setReportLoading(false);
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/prices', {
        params: {
          status: filters.status,
          method: filters.method !== 'all' ? filters.method : undefined,
          product_category_id: filters.categoryId || undefined,
          product_search: filters.productSearch || undefined,
          product_id: filters.productId || undefined,
          customer_price_group_id: filters.customerGroupId || undefined,
          uom_id: filters.uomId || undefined,
          start_date: filters.startDate || undefined,
          end_date: filters.endDate || undefined,
          page,
          limit,
        },
      });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setFormInitial(null);
    setShowForm(true);
  };

  const goToPricing = (item) => navigate(`/sales-prices/${item.id}`, { state: { browseIds: rows.map((r) => r.id) } });

  // Client-side duplication, same pattern as Products' kebab Duplicate:
  // strip id and open the same Add form pre-filled, so Save falls through
  // to a normal POST. Unlike Products there's no obvious field to
  // auto-adjust for uniqueness — Scope/UoM are all meaningful here — so the
  // user is left to change whichever one makes this a distinct Price; the
  // backend's overlap check (409) surfaces via the existing showError if
  // they save without changing anything.
  const handleDuplicate = (item) => {
    setFormInitial({ ...item, id: undefined, _duplicateSourceId: item.id });
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm('Permanently delete this Price? This cannot be undone.', { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/prices/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const toggleGroup = (key) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Same endpoint/behaviour as the Pricing page's own "Delete entire
  // Schedule" (PricePricingPage.js's handleDeleteLine) — deletes every
  // period for this Scope, not just the ones in `group.items`. That matters
  // whenever a filter (Status, dates, ...) is narrowing the list: group.items
  // is only what's currently shown, so the confirm can't quote an exact
  // count as safely as the Pricing page can (which always loads the full,
  // unfiltered schedule).
  const handleDeleteLine = async (group) => {
    const message = filters.status === 'all'
      ? `Permanently delete all ${group.items.length} price periods for this Customer/Product/UoM Scope? This cannot be undone.`
      : `Permanently delete every price period for this Customer/Product/UoM Scope — not just the ${group.items.length} shown under your current filter? This cannot be undone.`;
    if (!(await askConfirm(message, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/prices/${group.items[0].id}/line`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      const res = await axios.post('/api/prices', data);
      setShowForm(false);
      // Add only ever sets Scope/UoM — a brand new record always defaults
      // to Fixed Override with no price yet, so go straight to its Pricing
      // page rather than back to the list with no obvious next step.
      const created = res.data.data;
      navigate(`/sales-prices/${created.id}`);
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  // The drawer's field list — all 7, unrestricted, since a saved tab can
  // capture anything from broad (just a Customer Group) to precise (down
  // to one UoM), whether it's used daily or once a month. Separate from
  // the always-visible quick row below, which only surfaces the fields
  // that get touched ad hoc without wanting to save them.
  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Product Category</label><br />
        <select className="styled-input" value={draft.categoryId} onChange={(e) => setDraftField('categoryId', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{indentOptionLabel(c.name, c.depth)}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Product</label><br />
        <input
          placeholder="Search name or SKU..."
          value={draft.productSearch}
          onChange={(e) => setDraftField('productSearch', e.target.value)}
          style={FIELD_INPUT_STYLE}
        />
      </div>
      <div>
        <ProductExactPicker
          productId={draft.productId}
          productLabel={draft.productLabel}
          onChange={(id, label) => { setDraftField('productId', id); setDraftField('productLabel', label); }}
        />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Customer Price Group</label><br />
        <select className="styled-input" value={draft.customerGroupId} onChange={(e) => setDraftField('customerGroupId', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All groups</option>
          {customerGroups.map((g) => (
            <option key={g.id} value={g.id}>{indentOptionLabel(g.name, g.depth)}</option>
          ))}
        </select>
      </div>
      <ScopedUomSelect
        productId={draft.productId}
        value={draft.uomId}
        onChange={(val) => setDraftField('uomId', val)}
        fallbackUoms={uoms}
      />
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Method</label><br />
        <select className="styled-input" value={draft.method} onChange={(e) => setDraftField('method', e.target.value)} style={FIELD_INPUT_STYLE}>
          {METHOD_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Effective From</label><br />
        <DateInput value={draft.startDate} onChange={(e) => setDraftField('startDate', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Effective To</label><br />
        <DateInput value={draft.endDate} onChange={(e) => setDraftField('endDate', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
    </>
  );

  const showProductName = visibleColumns.includes('product_name');
  const showAvailability = visibleColumns.includes('availability');
  const showStatus = visibleColumns.includes('status');
  // Customer/Product/UoM/Type/Effective/actions (6) plus whichever optional
  // columns are currently on.
  const totalCols = 6 + OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).length;
  // Column widths (%) for each optional-column combination — the table is
  // tableLayout: fixed, so each set has to sum to 100. Order: Customer, SKU,
  // [Product Name], UoM, Type, [Availability], Effective, Status, actions.
  // A hidden Status hands its width to Effective, the column beside it.
  const widthsWithStatus = showProductName
    ? (showAvailability ? [16, 8, 21, 8, 13, 12, 12, 6, 4] : [20, 8, 28, 8, 14, 13, 5, 4])
    : (showAvailability ? [20, 16, 9, 17, 14, 14, 6, 4] : [20, 24, 10, 19, 15, 7, 5]);
  const colWidths = showStatus
    ? widthsWithStatus
    : [...widthsWithStatus.slice(0, -3), widthsWithStatus.at(-3) + widthsWithStatus.at(-2), widthsWithStatus.at(-1)];
  const availabilityCell = (item) => (item.product ? (
    <AvailabilityPill
      badge={item.product.availability_badge}
      date={item.product.availability_date}
      color={item.product.availability_badge_color}
      showDate
    />
  ) : null);
  // Matches the group header row's own chevron + 4px gap indent (15 + 4 =
  // 19px, on top of the cell's normal 8px padding) so a nested row's blank
  // leading cells still read as falling under the parent's Customer text
  // instead of starting flush left.
  const NESTED_INDENT = 8 + 15 + 4;

  // Shared by both the length-1 (ungrouped) and grouped render paths below,
  // so a scope with only one period renders byte-for-byte the same as
  // before grouping existed. `nested` blanks the Customer/Product/UoM cells
  // (already shown once on the group's own header row) rather than
  // repeating the same Scope on every sibling period.
  const renderPriceRow = (item, nested = false) => (
    <tr
      key={item.id}
      className="list-row"
      onClick={() => { if (!showForm) goToPricing(item); }}
      style={{ cursor: showForm ? 'default' : 'pointer' }}
      title={showForm ? undefined : 'Click to open Pricing'}
    >
      {nested ? (
        <>
          <td style={{ padding: '6px 8px 6px', paddingLeft: NESTED_INDENT, borderLeft: '2px solid #e3e7ee' }} />
          <td style={{ padding: '6px 8px' }} />
          {showProductName && <td style={{ padding: '6px 8px' }} />}
          <td style={{ padding: '6px 8px' }} />
        </>
      ) : (
        <>
          <td style={{ padding: '6px 8px', fontSize: 13 }} title={customerScopeTitle(item)}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <ChevronRight size={15} strokeWidth={2} style={{ visibility: 'hidden', flexShrink: 0 }} />
              {formatCustomerScope(item)}
            </span>
          </td>
          <td style={{ padding: '6px 8px', fontSize: 13 }} title={productScopeTitle(item)}>{formatProductScope(item)}</td>
          {showProductName && (
            <td style={{ padding: '6px 8px', fontSize: 13, maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.product?.name || undefined}>
              {item.product?.name || ''}
            </td>
          )}
          <td style={{ padding: '6px 8px', fontSize: 13 }}>{item.uom ? item.uom.name : '(All UoMs)'}</td>
        </>
      )}
      <td style={{ padding: '6px 8px', fontSize: 13 }} title={fullScheduleTitle(item)}><AdjustmentTypeCell item={item} /></td>
      {showAvailability && (nested
        ? <td style={{ padding: '6px 8px' }} />
        : <td style={{ padding: '6px 8px', fontSize: 13 }}>{availabilityCell(item)}</td>)}
      <td style={{ padding: '6px 8px', fontSize: 13, lineHeight: 1.5 }}><EffectiveDates item={item} /></td>
      {showStatus && (
        <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
          <StatusPill item={item} />
        </td>
      )}
      <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        <RowActionsMenu
          items={[
            { key: 'pricing', label: 'Pricing', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => goToPricing(item) },
            { key: 'duplicate', label: 'Duplicate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDuplicate(item) },
            { key: 'delete', label: 'Delete', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDelete(item) },
          ]}
        />
      </td>
    </tr>
  );

  // Header row for a Scope with 2+ periods — the parent row you asked for:
  // Customer/Product/UoM shown once, chevron expands/collapses the periods
  // underneath. A single-period Scope skips this entirely and renders
  // exactly as it always has (see renderPriceRow's length===1 branch below).
  const renderGroup = (group) => {
    const isCollapsed = !expandedGroups.has(group.key);
    const first = group.items[0];
    return (
      <React.Fragment key={group.key}>
        <tr
          className="list-row"
          onClick={() => toggleGroup(group.key)}
          style={{ cursor: 'pointer', fontWeight: 600, background: '#f6f8fa' }}
          title={isCollapsed ? 'Click to expand' : 'Click to collapse'}
        >
          <td style={{ padding: '6px 8px', fontSize: 13 }} title={customerScopeTitle(first)}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <ChevronRight size={15} strokeWidth={2} style={{ color: '#9aa5b5', flexShrink: 0, transform: isCollapsed ? 'none' : 'rotate(90deg)', transition: 'transform 0.15s' }} />
              {formatCustomerScope(first)}
            </span>
          </td>
          <td style={{ padding: '6px 8px', fontSize: 13 }} title={productScopeTitle(first)}>{formatProductScope(first)}</td>
          {showProductName && (
            <td style={{ padding: '6px 8px', fontSize: 13, maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={first.product?.name || undefined}>
              {first.product?.name || ''}
            </td>
          )}
          <td style={{ padding: '6px 8px', fontSize: 13 }}>{first.uom ? first.uom.name : '(All UoMs)'}</td>
          <td style={{ padding: '6px 8px', fontSize: 13 }}>{group.items.length} periods</td>
          {showAvailability && <td style={{ padding: '6px 8px', fontSize: 13 }}>{availabilityCell(first)}</td>}
          <td style={{ padding: '6px 8px', fontSize: 13 }}>—</td>
          {showStatus && <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>—</td>}
          <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <RowActionsMenu
              items={[
                { key: 'delete-line', label: 'Delete entire Schedule', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeleteLine(group) },
              ]}
            />
          </td>
        </tr>
        {!isCollapsed && group.items.map((item) => renderPriceRow(item, true))}
      </React.Fragment>
    );
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Sales Prices</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Price
            </button>
          )}
          <ReportLauncher
            options={[
              { key: 'report', label: 'Price Break Report', onSelect: () => openReport('break') },
              { key: 'margin-report', label: 'Margin Report', onSelect: () => openReport('margin') },
              { key: 'margin-matrix-report', label: 'Margin Matrix', onSelect: () => openReport('marginMatrix') },
            ]}
            activeContent={
              reportType === 'break' ? (
                <PriceBreakReportView items={reportRows} loading={reportLoading} filterSummary={reportFilterSummary} onClose={() => setReportType(null)} />
              ) : reportType === 'margin' ? (
                <MarginReportView items={reportRows} loading={reportLoading} filterSummary={reportFilterSummary} onClose={() => setReportType(null)} />
              ) : reportType === 'marginMatrix' ? (
                <MarginMatrixReportView items={reportRows} loading={reportLoading} filterSummary={reportFilterSummary} onClose={() => setReportType(null)} />
              ) : null
            }
            onCloseActive={() => setReportType(null)}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Search name or SKU..."
            value={filters.productSearch}
            onChange={(e) => updateFilter('productSearch', e.target.value)}
            style={{ width: 220 }}
          />
          {filters.productSearch && (
            <button type="button" onClick={() => updateFilter('productSearch', '')} title="Clear product filter" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <ColumnPicker
          screenKey={FILTER_SCREEN_KEY}
          columns={OPTIONAL_COLUMNS}
          defaultVisible={DEFAULT_VISIBLE_COLUMNS}
          onChange={setVisibleColumns}
          tabColumns={tabColumns}
          systemColumns={SYSTEM_COLUMNS}
        />
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          tabColumns={tabColumns}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the price list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <PriceForm
            key={formInitial?._duplicateSourceId ? `dup-${formInitial._duplicateSourceId}` : 'add'}
            initial={formInitial}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); dismissError(); }}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            {colWidths.map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Customer</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>SKU</th>
              {showProductName && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Product Name</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>UoM</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Type</th>
              {showAvailability && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Availability</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Effective</th>
              {showStatus && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : groupRows(rows).map((group) => (
              group.items.length === 1 ? renderPriceRow(group.items[0]) : renderGroup(group)
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Price Lines"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default PriceTable;
